import type { DraftEntry, DraftLedger } from "../domain/draft";
import type { ProjectionCoverage } from "../domain/coverage";
import type { AnonymousFixture, FixtureElement } from "../domain/types";
import { LOCAL_IFC_ARTIFACT, sha256Hex, type LocalIfcObservation, type LocalIfcSource } from "./local-ifc";

export interface ProjectionRegenerationEngine {
  validate(planJson: string, optionsJson: string): string;
  export(planJson: string): string;
  reimport(bytes: Uint8Array, filename: string, optionsJson: string): string;
}

export interface AppliedOccurrencePropertyChange {
  readonly workbenchElementId: string;
  readonly planElementId: string;
  readonly propertySet: string;
  readonly property: string;
  readonly dataType: string | null;
  readonly before: string | number | boolean;
  readonly after: string | number | boolean;
  readonly planValuePath: string;
}

export interface ProjectionRegenerationReceipt {
  readonly contractVersion: "workbench-local-ifc-regeneration/1";
  readonly label: "Regenerated IFC from supported BIM-JSON projection";
  readonly source: LocalIfcObservation["source"];
  readonly output: { readonly filename: string; readonly byteLength: number; readonly sha256: string };
  readonly artifact: typeof LOCAL_IFC_ARTIFACT;
  readonly input: {
    readonly baselinePlanSha256: string;
    readonly modifiedPlanSha256: string;
    readonly proposalSha256: string;
    readonly importReport: unknown;
    readonly projectionCoverage: ProjectionCoverage;
  };
  readonly operations: {
    readonly requested: number;
    readonly applied: number;
    readonly refused: number;
    readonly changes: readonly AppliedOccurrencePropertyChange[];
  };
  readonly validation: {
    readonly baselineIssueCount: number;
    readonly modifiedIssueCount: number;
    readonly blockingIssueCount: number;
    readonly unknown: unknown;
  };
  readonly reimport: {
    readonly requestedChangesFoundExactlyOnce: number;
    readonly planLeafClosure: {
      readonly total: number;
      readonly unchanged: number;
      readonly requested: number;
      readonly degraded: number;
      readonly skipped: number;
      readonly unsupported: number;
      readonly examples: readonly { readonly path: string; readonly outcome: "requested" | "degraded" }[];
    };
    readonly report: unknown;
    readonly unknownTopLevel: Readonly<Record<string, unknown>>;
  };
  readonly limitations: readonly string[];
  readonly resource: { readonly elapsedMs: number; readonly sourceBytes: number; readonly outputBytes: number };
}

export interface ProjectionRegenerationResult {
  readonly ifcBytes: Uint8Array;
  readonly receipt: ProjectionRegenerationReceipt;
}

type MutableRecord = Record<string, unknown>;

function isRecord(value: unknown): value is MutableRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function items(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function primitive(value: unknown): value is string | number | boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function parseRecord(json: string, label: string): MutableRecord {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error(`${label} returned invalid JSON.`);
  }
  if (!isRecord(parsed)) throw new Error(`${label} returned a non-object result.`);
  return parsed;
}

function rawPlan(observation: LocalIfcObservation): MutableRecord {
  if (observation.state !== "observed" || !isRecord(observation.raw) || !isRecord(observation.raw.plan)) {
    throw new Error("A successful local IFC observation with an object Plan is required.");
  }
  return observation.raw.plan;
}

function sourceIdentityCandidates(element: FixtureElement): readonly string[] {
  const sourceRecord = isRecord(element.unknown.sourceRecord) ? element.unknown.sourceRecord : null;
  if (sourceRecord === null) return [];
  return [...new Set([sourceRecord.id, sourceRecord.guid].filter((value): value is string => typeof value === "string" && value !== ""))];
}

interface PropertyMatch {
  readonly planElementId: string;
  readonly propertySet: MutableRecord;
  readonly propertySetName: string;
  readonly propertyValue: MutableRecord;
  readonly planValuePath: string;
}

function propertyMatches(plan: MutableRecord, elementIds: ReadonlySet<string>, propertyName: string): PropertyMatch[] {
  const matches: PropertyMatch[] = [];
  for (const [associationIndex, association] of items(plan.properties).entries()) {
    if (!isRecord(association) || typeof association.element_id !== "string" || !elementIds.has(association.element_id)) continue;
    for (const [setIndex, propertySet] of items(association.sets).entries()) {
      if (!isRecord(propertySet)) continue;
      const propertySetName = typeof propertySet.name === "string" ? propertySet.name : "";
      for (const [propertyIndex, propertyValue] of items(propertySet.props).entries()) {
        if (isRecord(propertyValue) && propertyValue.name === propertyName) {
          matches.push({
            planElementId: association.element_id,
            propertySet,
            propertySetName,
            propertyValue,
            planValuePath: `/properties/${associationIndex}/sets/${setIndex}/props/${propertyIndex}/value`
          });
        }
      }
    }
  }
  return matches;
}

function equalValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function applyEntry(plan: MutableRecord, fixture: AnonymousFixture, entry: DraftEntry): AppliedOccurrencePropertyChange {
  if (entry.source !== "property" || entry.operation !== "replace") {
    throw new Error(`${entry.elementId} · ${entry.property}: only existing occurrence property replacement is supported.`);
  }
  if (!primitive(entry.before) || !primitive(entry.after) || typeof entry.before !== typeof entry.after) {
    throw new Error(`${entry.elementId} · ${entry.property}: replacement must preserve the existing primitive value type.`);
  }
  const element = fixture.elements.find((candidate) => candidate.id === entry.elementId);
  if (element === undefined) throw new Error(`${entry.elementId}: element is not present in the active observation.`);
  const candidates = sourceIdentityCandidates(element);
  if (candidates.length === 0) throw new Error(`${entry.elementId}: producer Plan identity is unavailable.`);
  const matches = propertyMatches(plan, new Set(candidates), entry.property);
  if (matches.length !== 1) {
    throw new Error(`${entry.elementId} · ${entry.property}: expected one producer Plan property, found ${matches.length}.`);
  }
  const match = matches[0];
  if (!equalValue(match.propertyValue.value, entry.before)) {
    throw new Error(`${entry.elementId} · ${entry.property}: typed before-value no longer matches the producer Plan.`);
  }
  match.propertyValue.value = structuredClone(entry.after);
  return {
    workbenchElementId: entry.elementId,
    planElementId: match.planElementId,
    propertySet: match.propertySetName,
    property: entry.property,
    dataType: typeof match.propertyValue.data_type === "string" ? match.propertyValue.data_type : null,
    before: entry.before,
    after: entry.after,
    planValuePath: match.planValuePath
  };
}

function validationIssues(engine: ProjectionRegenerationEngine, plan: MutableRecord): readonly unknown[] {
  const result = parseRecord(engine.validate(JSON.stringify(plan), "{}"), "Plan validation");
  if (!Array.isArray(result.issues)) throw new Error("Plan validation omitted its issues array.");
  return result.issues;
}

function blockingIssue(issue: unknown): boolean {
  if (!isRecord(issue)) return false;
  const severity = typeof issue.severity === "string" ? issue.severity.toLowerCase() : "";
  return issue.rule === "plan-parse-error" || severity === "error" || severity === "fatal";
}

function unknownTopLevel(raw: MutableRecord): Readonly<Record<string, unknown>> {
  const { plan: _plan, report: _report, error: _error, ...unknown } = raw;
  return unknown;
}

function accountPlanLeafClosure(
  left: unknown,
  right: unknown,
  requestedPaths: ReadonlySet<string>
): ProjectionRegenerationReceipt["reimport"]["planLeafClosure"] {
  const counts = { unchanged: 0, requested: 0, degraded: 0, skipped: 0, unsupported: 0 };
  const examples: { path: string; outcome: "requested" | "degraded" }[] = [];
  const visit = (a: unknown, b: unknown, path: string): void => {
    if (Object.is(a, b)) {
      if (!Array.isArray(a) && !isRecord(a)) counts.unchanged += 1;
      return;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
      const length = Math.max(a.length, b.length);
      for (let index = 0; index < length; index += 1) visit(a[index], b[index], `${path}/${index}`);
      return;
    }
    if (isRecord(a) && isRecord(b)) {
      const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
      for (const key of keys) visit(a[key], b[key], `${path}/${key}`);
      return;
    }
    const normalizedPath = path || "/";
    const outcome = requestedPaths.has(normalizedPath) ? "requested" : "degraded";
    counts[outcome] += 1;
    if (examples.length < 40) examples.push({ path: normalizedPath, outcome });
  };
  visit(left, right, "");
  return { total: Object.values(counts).reduce((sum, value) => sum + value, 0), ...counts, examples };
}

function verifyReimportedChanges(plan: MutableRecord, changes: readonly AppliedOccurrencePropertyChange[]): number {
  let verified = 0;
  for (const change of changes) {
    const matches = propertyMatches(plan, new Set([change.planElementId]), change.property)
      .filter((match) => match.propertySetName === change.propertySet && equalValue(match.propertyValue.value, change.after));
    if (matches.length !== 1) {
      throw new Error(`${change.planElementId} · ${change.propertySet}.${change.property}: regenerated IFC did not reimport with the requested change exactly once.`);
    }
    verified += 1;
  }
  return verified;
}

function outputFilename(sourceFilename: string): string {
  const stem = sourceFilename.replace(/\.ifc$/i, "") || "model";
  return `${stem}.workbench.ifc`;
}

export async function regenerateLocalIfcProjection(
  engine: ProjectionRegenerationEngine,
  source: LocalIfcSource,
  observation: LocalIfcObservation,
  fixture: AnonymousFixture,
  projectionCoverage: ProjectionCoverage,
  draft: DraftLedger
): Promise<ProjectionRegenerationResult> {
  const startedAt = performance.now();
  if (draft.fixtureId !== fixture.id) throw new Error("Pending changes belong to a different observation.");
  if (draft.entries.length === 0) throw new Error("At least one pending property change is required.");
  if (source.filename !== observation.source.filename || source.bytes.byteLength !== observation.source.byteLength) {
    throw new Error("Retained source bytes do not match the active observation identity.");
  }
  const sourceDigest = await sha256Hex(source.bytes);
  if (sourceDigest !== observation.source.sha256) throw new Error("Retained source bytes failed the active observation digest check.");

  const baselinePlan = structuredClone(rawPlan(observation));
  const modifiedPlan = structuredClone(baselinePlan);
  const changes: AppliedOccurrencePropertyChange[] = [];
  for (const entry of draft.entries) changes.push(applyEntry(modifiedPlan, fixture, entry));

  const baselinePlanJson = JSON.stringify(baselinePlan);
  const modifiedPlanJson = JSON.stringify(modifiedPlan);
  const [baselinePlanSha256, modifiedPlanSha256, proposalSha256] = await Promise.all([
    sha256Hex(new TextEncoder().encode(baselinePlanJson)),
    sha256Hex(new TextEncoder().encode(modifiedPlanJson)),
    sha256Hex(new TextEncoder().encode(JSON.stringify(draft)))
  ]);
  const baselineIssues = validationIssues(engine, baselinePlan);
  const modifiedIssues = validationIssues(engine, modifiedPlan);
  const blockingIssues = modifiedIssues.filter(blockingIssue);
  if (blockingIssues.length > 0) throw new Error(`Modified Plan has ${blockingIssues.length} blocking validation issue(s).`);

  const ifcText = engine.export(modifiedPlanJson);
  if (!ifcText.startsWith("ISO-10303-21;")) {
    const detail = (() => {
      try {
        const parsed = JSON.parse(ifcText) as unknown;
        return isRecord(parsed) && typeof parsed.error === "string" ? parsed.error : "Exporter did not return IFC-SPF.";
      } catch {
        return "Exporter did not return IFC-SPF.";
      }
    })();
    throw new Error(detail);
  }
  const ifcBytes = new TextEncoder().encode(ifcText);
  const filename = outputFilename(source.filename);
  const reimportRaw = parseRecord(engine.reimport(ifcBytes, filename, LOCAL_IFC_ARTIFACT.importOptions), "Regenerated IFC reimport");
  if (!isRecord(reimportRaw.plan)) {
    throw new Error(isRecord(reimportRaw.report) && typeof reimportRaw.report.error === "string"
      ? `Regenerated IFC reimport failed: ${reimportRaw.report.error}`
      : "Regenerated IFC reimport omitted its Plan.");
  }
  const verified = verifyReimportedChanges(reimportRaw.plan, changes);
  const planLeafClosure = accountPlanLeafClosure(
    baselinePlan,
    reimportRaw.plan,
    new Set(changes.map((change) => change.planValuePath))
  );
  const outputDigest = await sha256Hex(ifcBytes);

  return {
    ifcBytes,
    receipt: {
      contractVersion: "workbench-local-ifc-regeneration/1",
      label: "Regenerated IFC from supported BIM-JSON projection",
      source: observation.source,
      output: { filename, byteLength: ifcBytes.byteLength, sha256: outputDigest },
      artifact: LOCAL_IFC_ARTIFACT,
      input: {
        baselinePlanSha256,
        modifiedPlanSha256,
        proposalSha256,
        importReport: structuredClone(isRecord(observation.raw) ? observation.raw.report : undefined),
        projectionCoverage: structuredClone(projectionCoverage)
      },
      operations: {
        requested: draft.entries.length,
        applied: changes.length,
        refused: draft.entries.length - changes.length,
        changes
      },
      validation: {
        baselineIssueCount: baselineIssues.length,
        modifiedIssueCount: modifiedIssues.length,
        blockingIssueCount: blockingIssues.length,
        unknown: structuredClone(modifiedIssues)
      },
      reimport: {
        requestedChangesFoundExactlyOnce: verified,
        planLeafClosure,
        report: structuredClone(reimportRaw.report),
        unknownTopLevel: structuredClone(unknownTopLevel(reimportRaw))
      },
      limitations: [
        "Local export — not saved or verified by BIM-Hub.",
        "Regenerated from the supported BIM-JSON projection; this is not a source whole-record lossless round trip.",
        "Only existing occurrence properties with the same primitive value type were replaced.",
        "Geometry, placement, representation, type, component, and classification changes were not applied."
      ],
      resource: {
        elapsedMs: Number((performance.now() - startedAt).toFixed(2)),
        sourceBytes: source.bytes.byteLength,
        outputBytes: ifcBytes.byteLength
      }
    }
  };
}
